// Frontend display derives from authoritative catalog/* — icons/effects are presentation only.
// This module provides UI-friendly arrays derived from the authoritative BigInt catalog
// so @cookies/shared/catalog/definitions can be removed. No gameplay logic here.

import { UPGRADES as AUTH_UPGRADES, type UpgradeDef as AuthUpgradeDef } from "./upgrades";
import { MODULES as AUTH_MODULES, type ModuleDef as AuthModuleDef } from "./modules";
import { ABILITIES as AUTH_ABILITIES, type AbilityDef as AuthAbilityDef } from "./abilities";
import { COSMETICS as AUTH_COSMETICS, type CosmeticDef as AuthCosmeticDef } from "./cosmetics";
import { QUESTS as AUTH_QUESTS, type QuestDef as AuthQuestDef } from "./quests";
import { escalateCost } from "../economy";

// Display shape (compat with old definitions.ts consumers). Costs are numbers for UI estimates;
// backend remains authoritative with BigInt strings on the wire.
export type DisplayUpgradeDef = {
  id: string;
  name: string;
  icon: string;
  desc: string;
  effect: string;
  baseCost: number;
  costMult: number;
  maxLevel: number;
  unlockLevel: number;
  specLocked?: string;
  requiresId?: string;
  requiresLevel?: number;
};

export type DisplayModuleDef = {
  id: string;
  name: string;
  icon: string;
  cps: string;
  desc: string;
  lockedLevel?: number;
  requiresId?: string;
  requiresLevel?: number;
};

export type DisplayAbilityDef = {
  id: string;
  name: string;
  icon: string;
  desc: string;
  effect: string;
  cost: number;
  cooldownSec: number;
  durationSec: number;
  unlockLevel: number;
  specLocked?: string;
};

export type DisplayCosmeticDef = {
  id: string;
  name: string;
  icon: string;
  kind: "skin" | "glow" | "ring" | "trail" | "sound";
  currency: "credits" | "cookies";
  cost: number;
  desc: string;
};

export type DisplayEventDef = { id: string; name: string; icon: string; desc: string; reward: string };

export type DisplayQuestDef = AuthQuestDef;

export type SpecDef = {
  id: string;
  name: string;
  icon: string;
  tool: string;
  desc: string;
  perks: string[];
};

// Icon / desc maps sourced from legacy definitions.ts (presentation only)
const UPGRADE_DISPLAY: Record<string, { icon: string; desc: string; effect: string }> = {
  stronger_hands: { icon: "✋", desc: "+3 click power", effect: "+3 click power / Lv" },
  faster_fingers: { icon: "🖐", desc: "+3 click power", effect: "+3 click power / Lv" },
  critical_knead: { icon: "🍪", desc: "5% chance for double cookies", effect: "+1% crit chance / Lv" },
  combo_training: { icon: "🔥", desc: "Builds combos faster", effect: "+3 click power / Lv" },
  golden_touch: { icon: "✨", desc: "Crits sparkle while you knead", effect: "+1.5% crit chance / Lv" },
  bakers_dozen: { icon: "🥯", desc: "Every 13th click bakes an extra batch", effect: "+4 click power, 13th click +50% / Lv" },
  sugar_rush: { icon: "⚡", desc: "Rushes surge longer while baking", effect: "+2 click power / Lv" },
  heavy_roller: { icon: "🧱", desc: "Flattens dough in bulk", effect: "+5 click power / Lv" },
  master_knead: { icon: "🥋", desc: "Decades of kneading technique", effect: "+8 click power / Lv" },
  crit_glaze: { icon: "🍩", desc: "Glazed cookies crit harder", effect: "+2% crit chance / Lv" },
  star_knead: { icon: "🌟", desc: "Knead with starlight", effect: "+10 click, +1% crit / Lv" },
  eternal_hands: { icon: "♾️", desc: "Hands that never tire", effect: "+15 click, +1.5% crit / Lv" },
  gilded_whisk: { icon: "🥄", desc: "Gold-plated whisk, now infused with prismatic sugar", effect: "+15 click, +8% clicks / Lv" },
  mythril_spatula: { icon: "🍴", desc: "Light mythril flipper for late ovens", effect: "+20 click, +10% clicks / Lv" },
  cosmic_glaze: { icon: "🌌", desc: "Glaze dusted with star sugar", effect: "+25 click, +12% clicks, +0.5% crit / Lv" },
  golden_apron: { icon: "🥇", desc: "Apron woven with golden thread — true prestige", effect: "+35 click, +15% clicks / Lv" },
  solar_whisk: { icon: "☀️", desc: "Whisk forged in solar heat", effect: "+50 click, +18% clicks / Lv" },
  void_spatula: { icon: "🕳️", desc: "Flipper that folds void sugar", effect: "+50 click, +22% clicks, +0.5% crit / Lv" },
  prismatic_whisk: { icon: "🌈", desc: "Chase: refracts every knead into a spectrum", effect: "+25 click, +12% clicks / Lv" },
  chrono_spatula: { icon: "⏳", desc: "Chase: flips dough through time", effect: "+40 click, +18% clicks / Lv" },
  stellar_charm: { icon: "💫", desc: "Chase: lucky star-charm for crit fishers", effect: "+25 click, +15% clicks, +1% crit / Lv" },
  divine_glaze: { icon: "😇", desc: "Chase: the final glaze — god-tier clicks", effect: "+60 click, +25% clicks, +0.5% crit / Lv" },
  oven_assistant: { icon: "🥣", desc: "Stirs the dough automatically while present", effect: "+2 cookies/sec while present / Lv" },
  auto_kneader: { icon: "🤖", desc: "Steady live kneading while you bake", effect: "+3 cookies/sec while present / Lv" },
  cookie_sorter: { icon: "📦", desc: "Sorts cookies for bonus while baking", effect: "+4% Baker Credits while present / Lv (max +80%)" },
  delivery_assistant: { icon: "🛵", desc: "Runs deliveries while you're at the oven", effect: "+4 cookies/sec while present / Lv" },
  oven_mastery: { icon: "🔧", desc: "Your live kneader evolves each level", effect: "+5 cookies/sec while present / Lv" },
  team_oven: { icon: "🤝", desc: "Co-op heat — stronger with more bakers present", effect: "+3/sec +1/sec per active baker / Lv" },
  fortune_dough: { icon: "🍀", desc: "Golden cookies shimmer more often", effect: "+3% golden cookie chance / Lv" },
  mech_kneader: { icon: "🦾", desc: "Pure CPS workhorse, no finesse", effect: "+7 cookies/sec while present / Lv" },
  sparkle_kneader: { icon: "💫", desc: "Hybrid: less CPS but boosts clicks", effect: "+3/sec +2 click / Lv" },
  sorter_gold: { icon: "🌟", desc: "Sorter branch: golden chance", effect: "+2/sec +2% crit / Lv" },
  sorter_credit: { icon: "🪙", desc: "Sorter branch: credit bonus counts double", effect: "+2/sec, double credit weight / Lv" },
  sorter_offline: { icon: "🗄️", desc: "Sorter branch: offline + high CPS", effect: "+6/sec, +1.5% offline efficiency / Lv" },
  oven_rush: { icon: "🔥", desc: "Oven tier: rush chance + CPS", effect: "+4/sec +1% crit / Lv" },
  oven_long: { icon: "⏳", desc: "Oven tier: slow long bake, high CPS", effect: "+7/sec / Lv" },
  oven_core: { icon: "☢️", desc: "Molten core of the bakery", effect: "+12/sec / Lv" },
  clockwork_bakery: { icon: "⏰", desc: "The bakery ticks on its own", effect: "+19/sec / Lv" },
  star_conveyor: { icon: "🚀", desc: "Conveyor fed by starlight", effect: "+32/sec / Lv" },
  crumb_vault: { icon: "🗝️", desc: "Deep vault stores crumbs — now a compounding engine", effect: "+25/sec, +8% CPS / Lv" },
  nebula_oven: { icon: "🌠", desc: "Bakes in nebula gas, compounding heat", effect: "+40/sec, +10% CPS / Lv" },
  eternal_conveyor: { icon: "♾️", desc: "Belt that never stops — and accelerates", effect: "+60/sec, +12% CPS / Lv" },
  quasar_oven: { icon: "💥", desc: "Bakes with quasar heat", effect: "+90/sec, +16% CPS / Lv" },
  bigbang_conveyor: { icon: "🌌", desc: "Conveyor born from the Big Bang", effect: "+140/sec, +20% CPS / Lv" },
  singularity_oven: { icon: "🕳️", desc: "Chase: bakes inside a singularity", effect: "+50/sec, +12% CPS / Lv" },
  temporal_conveyor: { icon: "⏰", desc: "Chase: delivers yesterday's cookies today", effect: "+85/sec, +18% CPS / Lv" },
  omega_core: { icon: "☢️", desc: "Chase: the final reactor core", effect: "+110/sec, +20% CPS, +0.5% crit / Lv" },
  galaxy_forge: { icon: "🌟", desc: "Chase: forges cookies from galaxies", effect: "+160/sec, +25% CPS / Lv" },
  baker_apprentice: { icon: "👨‍🍳", desc: "Baker-only click training", effect: "+3 click / Lv" },
  baker_journeyman: { icon: "🥖", desc: "Baker-only click training", effect: "+4 click / Lv" },
  baker_artisan: { icon: "🥐", desc: "Baker-only click mastery", effect: "+6 click / Lv" },
  baker_master: { icon: "🎖️", desc: "Baker-only click mastery", effect: "+10 click / Lv" },
  baker_grandmaster: { icon: "👑", desc: "Baker-only click legend", effect: "+15 click / Lv" },
  baker_legend: { icon: "🌠", desc: "Baker-only eternal legend", effect: "+24 click +1% crit / Lv" },
  auto_tinker: { icon: "🔩", desc: "Automation-only machines", effect: "+2/sec / Lv" },
  auto_mechanic: { icon: "⚙️", desc: "Automation-only machines", effect: "+4/sec / Lv" },
  auto_engineer: { icon: "🏗️", desc: "Automation-only factory", effect: "+7/sec / Lv" },
  auto_factory: { icon: "🏭", desc: "Automation-only factory", effect: "+10/sec / Lv" },
  auto_overlord: { icon: "🤖", desc: "Automation-only overlord", effect: "+17/sec / Lv" },
  auto_titan: { icon: "🦿", desc: "Automation-only titan", effect: "+30/sec / Lv" },
  eng_scaffold: { icon: "🪜", desc: "Engineer-only hybrid build", effect: "+2/sec +1 click / Lv" },
  eng_crane: { icon: "🏗️", desc: "Engineer-only hybrid build", effect: "+3/sec +1 click / Lv" },
  eng_foundry: { icon: "🔥", desc: "Engineer-only hybrid build", effect: "+5/sec +2 click / Lv" },
  eng_citadel: { icon: "🏰", desc: "Engineer-only hybrid build", effect: "+9/sec +3 click / Lv" },
  eng_metropolis: { icon: "🌆", desc: "Engineer-only hybrid build", effect: "+14/sec +5 click / Lv" },
  eng_wonder: { icon: "🗽", desc: "Engineer-only wonder", effect: "+25/sec +8 click / Lv" },
  fortune_charm: { icon: "🧧", desc: "Fortune-only luck", effect: "+1.5% crit / Lv" },
  fortune_clover: { icon: "🍀", desc: "Fortune-only luck", effect: "+2% crit / Lv" },
  fortune_horseshoe: { icon: "🧲", desc: "Fortune-only luck + CPS", effect: "+2/sec +2.5% crit / Lv" },
  fortune_gem: { icon: "💎", desc: "Fortune-only luck + CPS", effect: "+3/sec +3% crit / Lv" },
  fortune_star: { icon: "⭐", desc: "Fortune-only luck + CPS", effect: "+4/sec +3.5% crit / Lv" },
  fortune_eclipse: { icon: "🌒", desc: "Fortune-only eclipse", effect: "+7/sec +5% crit / Lv" },
  coord_rally: { icon: "📢", desc: "Coordinator-only team bonus", effect: "+2/sec +0.5% crit / Lv" },
  coord_march: { icon: "🥁", desc: "Coordinator-only team bonus", effect: "+3/sec +1 click / Lv" },
  coord_festival: { icon: "🎪", desc: "Coordinator-only team bonus", effect: "+5/sec +1 click / Lv" },
  coord_carnival: { icon: "🎡", desc: "Coordinator-only team bonus", effect: "+9/sec +2 click / Lv" },
  coord_summit: { icon: "⛰️", desc: "Coordinator-only team bonus", effect: "+14/sec +3 click / Lv" },
  coord_utopia: { icon: "🌈", desc: "Coordinator-only utopia", effect: "+23/sec +5 click / Lv" },
  baker_mythic: { icon: "🏆", desc: "Baker-only mythic prestige", effect: "+30 click, +12% clicks / Lv" },
  auto_colossus: { icon: "🗿", desc: "Automation-only prestige frame", effect: "+45/sec, +12% CPS / Lv" },
  eng_paragon: { icon: "🏛️", desc: "Engineer-only prestige build", effect: "+12 click, +30/sec, +8% both / Lv" },
  fortune_comet: { icon: "☄️", desc: "Fortune-only lucky comet", effect: "+25/sec, +8% CPS, +1% crit / Lv" },
  coord_harmony: { icon: "🎶", desc: "Coordinator-only prestige chorus", effect: "+12 click, +30/sec, +8% both / Lv" },
  baker_eternal: { icon: "♾️", desc: "Baker-only eternal mastery", effect: "+40 click, +16% clicks / Lv" },
  auto_behemoth: { icon: "🦣", desc: "Automation-only behemoth frame", effect: "+70/sec, +16% CPS / Lv" },
  eng_apex: { icon: "🏔️", desc: "Engineer-only apex build", effect: "+20 click, +55/sec, +10/12% / Lv" },
  fortune_nova: { icon: "💥", desc: "Fortune-only stellar nova", effect: "+45/sec, +12% CPS, +1.2% crit / Lv" },
  coord_zenith: { icon: "🌠", desc: "Coordinator-only zenith chorus", effect: "+20 click, +55/sec, +10/12% / Lv" },
};

const MODULE_DISPLAY: Record<string, { icon: string; cps: string; desc: string; lockedLevel?: number }> = {
  oven: { icon: "🔥", cps: "+2/sec", desc: "Bakes batches — core CPS" },
  mixer: { icon: "🥣", cps: "+1/sec", desc: "Mixes dough — boosts base. Requires Oven Lv5" },
  packaging: { icon: "📦", cps: "+1/sec", desc: "Wraps cookies — adds delivery bonus" },
  delivery: { icon: "🛵", cps: "+4/sec", desc: "Ships orders — delivery bonus. Requires Packaging Lv5", lockedLevel: 2 },
  event_kitchen: { icon: "🎪", cps: "+2/sec", desc: "Hosts events — bonus rounds. Requires Oven Lv10", lockedLevel: 5 },
  research_lab: { icon: "🔬", cps: "+10/sec", desc: "Researches recipes — unlocks upgrades. Requires Mixer Lv10", lockedLevel: 6 },
  blast_furnace: { icon: "🌋", cps: "+25/sec", desc: "Extreme heat — massive CPS. Requires Oven Lv15", lockedLevel: 7 },
  quantum_oven: { icon: "⚛️", cps: "+60/sec", desc: "Bakes in parallel timelines. Requires Blast Furnace Lv5", lockedLevel: 8 },
  star_forge: { icon: "🌟", cps: "+150/sec", desc: "Forges cookies from starlight. Requires Quantum Oven Lv5", lockedLevel: 9 },
  guild_hall: { icon: "🏛️", cps: "+400/sec", desc: "Eternal bakery monument. Requires Star Forge Lv5", lockedLevel: 10 },
};

const ABILITY_DISPLAY: Record<string, { icon: string; effect: string; desc: string }> = {
  click_frenzy: { icon: "👆", effect: "2x clicks · 30s", desc: "Your clicks are worth 2x for 30s." },
  auto_boost: { icon: "🔥", effect: "2x automation · 60s", desc: "Your automation produces 2x for 60s." },
  golden_luck: { icon: "🌟", effect: "Spawns golden cookie", desc: "Conjures a golden cookie right now." },
  rush_extend: { icon: "📢", effect: "+20s rush", desc: "Extends an active Cookie Rush by 20s (or starts a 20s rush)." },
  emergency_bake: { icon: "🚨", effect: "Instant cookies + credits", desc: "Instantly bake 60s of bakery production plus a click-power batch." },
};

const COSMETIC_DISPLAY: Record<string, string> = {
  white_chocolate: "🤍",
  dark_chocolate: "🤎",
  strawberry: "🍓",
  matcha: "🍵",
  midnight: "🌌",
  golden: "🌟",
  starlight: "✨",
  soft_gold_glow: "🕯️",
  moonlit_glow: "🌙",
  bronze_ring: "⭕",
  gilded_ring: "💍",
  starlit_ring: "💫",
  crumb_trail: "🍪",
  spark_trail: "⚡",
  comet_trail: "☄️",
  classic_pop: "📢",
  robotic: "🤖",
  reverb: "🌊",
  bubble: "🫧",
  arcade: "👾",
  cosmic_chime: "🌌",
};

function authToDisplay(a: AuthUpgradeDef): DisplayUpgradeDef {
  const d = UPGRADE_DISPLAY[a.id] ?? { icon: "🔧", desc: a.name, effect: "" };
  return {
    id: a.id,
    name: a.name,
    icon: d.icon,
    desc: d.desc,
    effect: d.effect,
    baseCost: Number(a.baseCost),
    costMult: 1.5,
    maxLevel: a.maxLevel,
    unlockLevel: a.unlockBakeryLevel,
    specLocked: a.specLocked,
    requiresId: a.requires?.id,
    requiresLevel: a.requires?.level,
  };
}

function moduleToDisplay(m: AuthModuleDef): DisplayModuleDef {
  const d = MODULE_DISPLAY[m.id] ?? { icon: "🏭", cps: `+${String(m.baseCps)}/sec`, desc: m.name };
  return {
    id: m.id,
    name: m.name,
    icon: d.icon,
    cps: d.cps,
    desc: d.desc,
    lockedLevel: d.lockedLevel,
    requiresId: m.requires?.id,
    requiresLevel: m.requires?.level,
  };
}

function abilityToDisplay(a: AuthAbilityDef): DisplayAbilityDef {
  const d = ABILITY_DISPLAY[a.id] ?? { icon: "✨", effect: a.description, desc: a.description };
  return {
    id: a.id,
    name: a.name,
    icon: d.icon,
    desc: a.description,
    effect: d.effect,
    cost: Number(a.cost),
    cooldownSec: Math.floor(a.cooldownMs / 1000),
    durationSec: Math.floor(a.durationMs / 1000),
    unlockLevel: a.unlockBakerLevel,
    specLocked: a.specLocked,
  };
}

function cosmeticToDisplay(c: AuthCosmeticDef): DisplayCosmeticDef {
  return {
    id: c.id,
    name: c.name,
    icon: COSMETIC_DISPLAY[c.id] ?? "✨",
    kind: c.kind,
    currency: c.currency,
    cost: Number(c.cost),
    desc: c.description,
  };
}

// Derived UI arrays — backend authoritative kind/specLocked, no hard-coded filter lists.
const allUpgrades = Object.values(AUTH_UPGRADES).map(authToDisplay);
export const UPGRADES: DisplayUpgradeDef[] = allUpgrades.filter((u) => {
  const a = AUTH_UPGRADES[u.id];
  return a !== undefined && a.kind === "click" && !a.specLocked;
});
export const AUTOMATION: DisplayUpgradeDef[] = allUpgrades.filter((u) => {
  const a = AUTH_UPGRADES[u.id];
  return a !== undefined && a.kind === "automation" && !a.specLocked;
});
export const SPEC_UPGRADES: DisplayUpgradeDef[] = allUpgrades.filter((u) => {
  const a = AUTH_UPGRADES[u.id];
  return a !== undefined && !!a.specLocked;
});

export const MODULES: DisplayModuleDef[] = Object.values(AUTH_MODULES).map(moduleToDisplay);
export const ABILITIES: DisplayAbilityDef[] = Object.values(AUTH_ABILITIES).map(abilityToDisplay);
export const COSMETICS: DisplayCosmeticDef[] = Object.values(AUTH_COSMETICS).map(cosmeticToDisplay);
export const QUESTS: DisplayQuestDef[] = Object.values(AUTH_QUESTS);
// Legacy EVENTS array shape for SettingsPage (icon/desc/reward).
export const EVENTS: DisplayEventDef[] = [
  { id: "cookie_rush", name: "Cookie Rush", icon: "🔥", desc: "All production increased by 10%.", reward: "+10% cookies, +25% click effects" },
  { id: "golden_cookie", name: "Golden Cookie!", icon: "🌟", desc: "One tap redeems — then 10s of 5x clicks!", reward: "5x clicks for 10s, bonus Baker Credits" },
  { id: "oven_overheat", name: "Oven Overheat", icon: "🥵", desc: "The bakery must work together to cool the oven.", reward: "Engineers repair for bonus credits" },
  { id: "lucky_batch", name: "Lucky Batch", icon: "🍀", desc: "Rare cookies are appearing!", reward: "Fortune players improve outcome" },
  { id: "sugar_rush", name: "Sugar Rush", icon: "⚡", desc: "Click combos build twice as fast.", reward: "2x combo speed for 60s" },
];

export const SPECIALIZATIONS: SpecDef[] = [
  { id: "baker", name: "Baker", icon: "🍪", tool: "✋ bare hand", desc: "Stronger active clicking. Better combos.", perks: ["+25% click power", "Faster combos", "Higher manual contribution"] },
  { id: "automation", name: "Automation", icon: "🤖", tool: "🧤 mechanical glove", desc: "Better live production and machines.", perks: ["+30% live production", "Stronger live kneaders", "Improved machines"] },
  { id: "engineer", name: "Engineer", icon: "🛠", tool: "🔧 wrench", desc: "Build faster. Improve infrastructure.", perks: ["Reduced project cost", "Faster construction", "Better infrastructure"] },
  { id: "fortune", name: "Fortune", icon: "🍀", tool: "🧤 golden glove", desc: "Better rare events and golden cookies.", perks: ["Better event chances", "Improved golden rewards", "Higher crit rewards"] },
  { id: "coordinator", name: "Coordinator", icon: "📋", tool: "📢 clipboard / megaphone", desc: "Stronger group and co-op bonuses.", perks: ["+10% team bonuses", "Better co-op rewards", "Improved team events"] },
];

export const DEV_PATHS = [
  { id: "industrial", name: "Industrial", icon: "🏭", desc: "Focus on production and automation." },
  { id: "community", name: "Community", icon: "🤝", desc: "Focus on group bonuses and cooperation." },
  { id: "fortune", name: "Fortune", icon: "🍀", desc: "Focus on rare events and lucky rewards." },
] as const;

export function costFor(def: DisplayUpgradeDef, level: number): number {
  const lv = Math.max(0, Math.floor(level));
  if (lv === 0) return def.baseCost;
  return Number(escalateCost(BigInt(def.baseCost), lv));
}
export function costForStr(def: DisplayUpgradeDef, level: number): string {
  return escalateCost(BigInt(def.baseCost), Math.max(0, Math.floor(level))).toString();
}

export type UpgradeDef = DisplayUpgradeDef;
export type ModuleDef = DisplayModuleDef;
export type AbilityDef = DisplayAbilityDef;
export type CosmeticDef = DisplayCosmeticDef;
export type QuestDef = DisplayQuestDef;
export type EventDef = DisplayEventDef;
